import { resolveThemeFontTokens } from './resolveThemeFontTokens';

const FONT_FAMILY_ROLE_KEYS = new Set(['display', 'heading', 'body']);

export interface ThemeConfig {
  cssVariables?: Record<string, string>;
  font?: {
    family?: Record<string, string>;
  };
  monetization?: Record<string, unknown>;
}

export interface ThemeConfigV2 {
  cssVariables?: Record<string, string>;
  spacing?: Record<string, string>;
  borderRadius?: Record<string, string>;
  shadow?: Record<string, string>;
  colors?: Record<string, Record<string, string>>;
  transition?: {
    duration?: Record<string, string>;
    timingFunction?: Record<string, string>;
  };
  font?: {
    family?: Record<string, string | null>;
    size?: Record<string, string>;
    lineHeight?: Record<string, string>;
    headingLevels?: Partial<
      Record<
        'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
        'display' | 'heading' | 'body' | null
      >
    > | null;
  };
  designTokenInventory?: {
    typography?: {
      typographyState?: Record<
        string,
        { fontFamily?: string | null; fontWeight?: string | null } | null
      >;
    };
  };
  breakpoints?: Record<string, { fontSize?: Record<string, string> }>;
}

/**
 * Converts a ThemeConfig object to a CSS custom properties string
 * Styles are scoped to :root, or [data-de] if the scoped parameter is true.
 * Hex values without a leading # are normalised automatically.
 * The --color- prefix is stripped so variables align with Tailwind tokens
 * (e.g. --color-primary-500 becomes --primary-500).
 */
export function convertThemeToCSS(
  theme: ThemeConfig,
  scoped: boolean = false,
): string {
  const cssRules: string[] = [];

  if (theme.cssVariables) {
    for (const [key, value] of Object.entries(theme.cssVariables)) {
      if (typeof value === 'string') {
        const formatted = /^[0-9A-Fa-f]{6}$/.test(value) ? `#${value}` : value;
        cssRules.push(`  ${key}: ${formatted};`);
      }
    }
  }

  if (theme.font?.family) {
    for (const [key, value] of Object.entries(theme.font.family)) {
      if (typeof value === 'string') {
        if (key.startsWith('--')) {
          cssRules.push(`  ${key}: ${value};`);
        } else {
          const cssVar = `--font-family-${key
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase()}`;
          cssRules.push(`  ${cssVar}: ${value};`);
        }
      }
    }
  }

  if (theme.monetization) {
    const process = (obj: Record<string, unknown>, prefix: string) => {
      for (const [key, value] of Object.entries(obj)) {
        const varName = `--${prefix}-${key
          .replace(/([A-Z])/g, '-$1')
          .toLowerCase()}`;
        if (typeof value === 'string') {
          cssRules.push(`  ${varName}: ${value};`);
        } else if (typeof value === 'object' && value !== null) {
          process(
            value as Record<string, unknown>,
            `${prefix}-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`,
          );
        }
      }
    };
    process(theme.monetization, 'monetization');
  }

  if (cssRules.length === 0) return '';

  return `${scoped ? '[data-de]' : ':root'} {\n${cssRules.join('\n')}\n}\n`.replace(
    /--color-/g,
    '--',
  );
}

/**
 * Converts a v2 ThemeConfig to a CSS custom properties string.
 *
 * Handles all first-class v2 sections:
 *   cssVariables, spacing, borderRadius, shadow, colors, transition, and font.
 *
 * The `breakpoints` section requires media queries and is intentionally
 * omitted here — callers that need responsive font-size overrides should
 * generate those blocks separately from `theme.breakpoints`.
 */
export function convertThemeV2ToCSS(
  theme: ThemeConfigV2,
  scoped: boolean = false,
): string {
  const cssRules: string[] = [];
  const selector = scoped ? '[data-de]' : ':root';
  const resolvedFonts = resolveThemeFontTokens({
    fontFamily: theme.font?.family,
    typographyState: theme.designTokenInventory?.typography?.typographyState,
    cssVariables: theme.cssVariables,
    headingLevels: theme.font?.headingLevels,
  });
  const resolvedFontKeys = new Set(Object.keys(resolvedFonts.variables));

  if (theme.cssVariables) {
    for (const [key, value] of Object.entries(theme.cssVariables)) {
      if (typeof value !== 'string' || resolvedFontKeys.has(key)) continue;
      const formatted = /^[0-9A-Fa-f]{6}$/.test(value) ? `#${value}` : value;
      cssRules.push(`  ${key}: ${formatted};`);
    }
  }

  if (theme.spacing) {
    for (const [key, value] of Object.entries(theme.spacing)) {
      if (typeof value === 'string') {
        cssRules.push(`  --spacing-${key}: ${value};`);
      }
    }
  }

  if (theme.borderRadius) {
    for (const [key, value] of Object.entries(theme.borderRadius)) {
      if (typeof value === 'string') {
        cssRules.push(`  --border-radius-${key}: ${value};`);
      }
    }
  }

  if (theme.shadow) {
    for (const [key, value] of Object.entries(theme.shadow)) {
      if (typeof value === 'string') {
        cssRules.push(`  --shadow-${key}: ${value};`);
      }
    }
  }

  if (theme.colors) {
    for (const [palette, shades] of Object.entries(theme.colors)) {
      if (shades && typeof shades === 'object') {
        for (const [shade, value] of Object.entries(shades)) {
          if (typeof value === 'string' && value.trim().length > 0) {
            cssRules.push(`  --${palette}-${shade}: ${value};`);
          }
        }
      }
    }
  }

  if (theme.transition?.duration) {
    for (const [key, value] of Object.entries(theme.transition.duration)) {
      if (typeof value === 'string') {
        cssRules.push(`  --transition-duration-${key}: ${value};`);
      }
    }
  }

  if (theme.transition?.timingFunction) {
    for (const [key, value] of Object.entries(
      theme.transition.timingFunction,
    )) {
      if (typeof value === 'string') {
        const cssKey =
          key === 'DEFAULT'
            ? 'default'
            : key.replace(/([A-Z])/g, '-$1').toLowerCase();
        cssRules.push(`  --transition-timing-${cssKey}: ${value};`);
      }
    }
  }

  for (const [key, value] of Object.entries(resolvedFonts.variables)) {
    cssRules.push(`  ${key}: ${value};`);
  }

  if (theme.font?.family) {
    for (const [key, value] of Object.entries(theme.font.family)) {
      if (typeof value !== 'string' || FONT_FAMILY_ROLE_KEYS.has(key)) continue;
      cssRules.push(`  --font-family-${key}: ${value};`);
    }
  }

  if (theme.font?.size) {
    for (const [key, value] of Object.entries(theme.font.size)) {
      if (typeof value === 'string') {
        cssRules.push(`  --font-size-${key}: ${value};`);
      }
    }
  }

  if (theme.font?.lineHeight) {
    for (const [key, value] of Object.entries(theme.font.lineHeight)) {
      if (typeof value === 'string') {
        cssRules.push(`  --font-line-height-${key}: ${value};`);
      }
    }
  }

  if (cssRules.length === 0) return '';
  return `${selector} {\n${cssRules.join('\n')}\n}\n`;
}
